/**
 * Geometry for the design band — the art-direction card that sits immediately
 * ABOVE and OUTSIDE the table on the board canvas.
 *
 * The whole thing rests on one fact from `board-space.ts`: the table is pinned
 * at `GRID_ORIGIN = {x:0, y:0}` and grows DOWNWARD as rows are added. Board
 * space is signed and unbounded, so everything above `y = 0` is permanently
 * free and the band never has to move as the script grows. Only the band's
 * WIDTH depends on the measured table, because column widths fold in
 * client-side text measurement (F-26 auto-widen).
 *
 * Pure — no React, no doc access — so the one number that is easy to get wrong
 * (the clearance) is unit-tested rather than eyeballed in a browser.
 */
import { GRID_ORIGIN } from "@kola/shared";

import { TABLE_BORDER } from "../../core/table.js";

/**
 * Vertical gap between the band's bottom edge and the table's top edge, in
 * board units.
 *
 * LOAD-BEARING, NOT COSMETIC. GridTable renders an invisible column
 * hover-handle gutter spanning `top: -GUTTER_SCREEN_PX / zoom` above the table
 * (`GridTable.tsx`, `GUTTER_SCREEN_PX = 26`). In BOARD units that strip is
 * `26 / zoom` tall, so at the minimum zoom of 0.25 it reaches 104 board px.
 * Anything inside it competes with the F-12b insert pills and add-bubbles for
 * the pointer, and the user loses either the band or their column handles
 * depending on z-order. 120 clears it with room to spare at every zoom.
 */
export const BAND_CLEARANCE = 120;

/**
 * Ceiling on the card's height in board units. The card SIZES TO ITS CONTENT
 * (it is bottom-anchored — see `designCardRect`), because a fixed height leaves
 * a half-filled art direction sitting in an obviously empty box; this only
 * stops a very long one from covering the board.
 */
export const CARD_MAX_HEIGHT = 340;

/**
 * Card width. Clamped rather than "as wide as the table": a 2-column board's
 * table is ~240 px wide and a card that narrow is unreadable, while a 40-column
 * board's is thousands and a card that wide is a line-length disaster.
 */
export const CARD_MIN_WIDTH = 560;
export const CARD_MAX_WIDTH = 1080;

/**
 * The card's placement in board units. There is no `h`: the card is pinned by
 * its BOTTOM edge and grows upward with its content (the renderer sets `top`
 * to `bottom` and pulls the box up by its own height with a CSS transform), so
 * its height is whatever the content needs and the clearance below it is
 * exact either way.
 */
export interface BandRect {
  x: number;
  /** Board y of the card's bottom edge. Negative — the card is above the table. */
  bottom: number;
  w: number;
}

/**
 * The measured table extent this module needs. A subset of
 * `GridLayoutSnapshot` so callers can pass that straight in, and so a test
 * does not have to build a whole snapshot.
 */
export interface TableExtent {
  originX: number;
  colEdges: readonly number[];
}

/**
 * Total table width in board units, or `undefined` when the grid has not
 * published a layout yet. `colEdges[n]` is the right CONTENT edge; the table's
 * outer edge adds the border the wrapper draws on each side.
 */
export function tableWidth(extent: TableExtent | null | undefined): number | undefined {
  const right = extent?.colEdges[extent.colEdges.length - 1];
  if (right === undefined) return undefined;
  return right + TABLE_BORDER;
}

/**
 * Where the art-direction card goes, in board units.
 *
 * `extent` may be null (the grid has not mounted or has not measured yet): the
 * card then falls back to its minimum width rather than not rendering. A
 * missing measurement is a reason to be conservative about WIDTH, never a
 * reason to leave the board's design theme invisible.
 */
export function designCardRect(
  extent: TableExtent | null | undefined,
  shotCount = 0,
): BandRect {
  const measured = tableWidth(extent);
  const w =
    measured === undefined
      ? CARD_MIN_WIDTH
      : Math.min(CARD_MAX_WIDTH, Math.max(CARD_MIN_WIDTH, measured));
  return {
    x: extent?.originX ?? GRID_ORIGIN.x,
    bottom: cardBottomWithShots(shotCount),
    w,
  };
}

/**
 * The band's bottom edge — negative by construction, sitting `BAND_CLEARANCE`
 * above the table's top. Also the boundary future rows of generated key visuals
 * (phase 2) stack upward from, so they never intrude on the clearance either.
 */
export function bandBottom(): number {
  return GRID_ORIGIN.y - BAND_CLEARANCE;
}

/* ------------------------- generated key visuals ------------------------ */

/** One key visual's board-unit size. 16:9, matching what the job asks for. */
export const SHOT_WIDTH = 288;
export const SHOT_HEIGHT = 162;
/** Gap between adjacent shots, and between the shot strip and the card above. */
export const SHOT_GAP = 12;

/**
 * Where the run's key visuals go: a left-to-right strip whose BOTTOM sits on
 * the same clearance line as the card, with the card floating above it.
 *
 * Board-space rectangles, one per shot, in run order. The strip grows
 * rightward from the table's left edge — it does not wrap, because a mood
 * board is read as a row and four frames at 288 board px is narrower than most
 * tables anyway.
 */
export function shotRects(
  shots: number | ReadonlyArray<{ w: number; h: number }>,
  extent: TableExtent | null | undefined,
): BandRect[] {
  const x0 = extent?.originX ?? GRID_ORIGIN.x;
  const bottom = bandBottom();
  // F-142: a frame keeps its own shape at the strip's height — a Shorts
  // week's vertical pages stand in a row of 9:16 tiles instead of being
  // cropped into 16:9 boxes. A count (or a size the doc does not know) is
  // the 16:9 default.
  const widths =
    typeof shots === "number"
      ? Array.from({ length: shots }, () => SHOT_WIDTH)
      : shots.map((shot) =>
          shot.w > 0 && shot.h > 0 ? Math.max(48, Math.round((SHOT_HEIGHT * shot.w) / shot.h)) : SHOT_WIDTH,
        );
  let x = x0;
  return widths.map((w) => {
    const rect = { x, bottom, w };
    x += w + SHOT_GAP;
    return rect;
  });
}

/**
 * The card's bottom edge once key visuals exist — it floats above the strip
 * rather than overlapping it. With no shots the card sits on the clearance
 * line itself, exactly as in phase one.
 */
export function cardBottomWithShots(shotCount: number): number {
  return shotCount === 0 ? bandBottom() : bandBottom() - SHOT_HEIGHT - SHOT_GAP;
}

/**
 * Where the card's top should land on screen when the view is framed for it.
 *
 * Not an arbitrary gap: the board-bar pills float ABSOLUTELY over the canvas at
 * `top: 12` with `height: 38` (`.board-bar` in app.css), so they occupy screen
 * y 12–50 and take no layout space of their own. Framing the card any higher
 * tucks its header under the board title.
 */
export const FRAME_TOP_MARGIN = 68;

/**
 * Screen-space pan needed to bring the card into view, or 0 when it already is.
 *
 * WHY THIS EXISTS. The board's default camera frames the table at the top-left,
 * and the card lives ABOVE the table — so on every fresh open it sits entirely
 * off-screen and the board's design theme is invisible to anyone who does not
 * already know to pan up. The camera is not persisted, so this is not an edge
 * case: it is what every load looked like.
 *
 * Returns a `panBy` dy. Pure so the framing rule is testable without a camera.
 */
export function framingPanDy(
  cameraY: number,
  zoom: number,
  cardTopBoard: number,
  margin = FRAME_TOP_MARGIN,
): number {
  const topOnScreen = (cardTopBoard - cameraY) * zoom;
  // Already clear of the floating board bar — never move a camera that does
  // not need it.
  if (topOnScreen >= margin) return 0;
  return margin - topOnScreen;
}
